import { log, callAdminAjax, getTemplate, nextFrame, createTranslator } from './utils.js';
import { getDirectoryRootMediaGallery_swiperInstance } from './hulk.js';
import { updatePostMediaGalleryItem, createMediaItemReport } from "./user-media-uploads.js";
import { setFenixBackdrop, openFenixModal, setFenixModalContent, closeFenixModal } from "./fenixModal.js";
import { showActionMenuModal } from "./common.js";

const $ = window.jQuery;
const t = createTranslator('common');

/* =========================================================
   Module-level configuration & state
   ========================================================= */

const GALLERY_CONFIG = {
    PAGE_SIZE: 20,
    LOAD_MORE_THRESHOLD: 5,
};

let currentGalleryKey = null;
let currentGalleryItemIndex = null;
let inFlightActions = {
    deleteImage: [],
    like: [],
    unlike: [],
    addCaption: [],
    editCaption: [],
    addTag: [],
    removeTag: [],
    duplicateMedia: [],
    makePrimary: [],
    updateMediaChild: [],
};

// Holder for the gallery states. This is where we store all the details for each instantiated gallery. Keyed by galleryKey.
var galleryStates = {};

var userMediaGalleryLikesSet = null;

/* =========================================================
   Init
   ========================================================= */

export function initPostMediaGalleries() {
    log('initPostMediaGalleries');
    if (!window.ironman.postMediaGallery) return;

    bindThumbnailClicks();
    bindOverlayClicks();

    // Convert the userMediaGalleryLikes array to a set for faster lookup.
    userMediaGalleryLikesSet = new Set(window.ironman.userData?.userMediaGalleryLikes || []);
}

/* =========================================================
   Likes Management (Local Set)
   ========================================================= */

/**
 * Check if the user has liked a media gallery item.
 * @param {*} mediaGalleryItemId 
 * @returns boolean
 */
function isLikedByUser(mediaGalleryItemId) {
    return userMediaGalleryLikesSet?.has(mediaGalleryItemId) || false;
}

/**
 * Add a user like to the media gallery item.
 * @param {*} mediaGalleryItemId 
 * @returns void
 */
function addUserLike(mediaGalleryItemId) {
    userMediaGalleryLikesSet.add(mediaGalleryItemId);
}

/**
 * Remove a user like from the media gallery item.
 * @param {*} mediaGalleryItemId 
 * @returns void
 */
function removeUserLike(mediaGalleryItemId) {
    userMediaGalleryLikesSet.delete(mediaGalleryItemId);
}

/* =========================================================
   State Management
   ========================================================= */

function normalizeGalleryKey(key) {
    if (key === null || key === undefined || key === "" || key === "null") {
        return "root";
    }
    return String(key);
}

/**
 * Return a key of the galleryStates object or create a new one.
 * @param {} galleryKey 
 * @returns Object
 */
function getState(galleryKey) {
    galleryKey = normalizeGalleryKey(galleryKey);

    // Create a new state object for the gallery if it doesn't exist.
    if (!galleryStates[galleryKey]) {
        galleryStates[galleryKey] = {
            key: galleryKey,
            total: 0,
            media: [],
            loading: false,
            allLoaded: false,
            lgInstance: null,
            lgContainer: null,
            $ui: null
        };
    }

    // Fetch the state object for the gallery.
    var gallery = galleryStates[galleryKey];

    // Update the state object from the sources of truth every time it's fetched.
    var src = (window.ironman && window.ironman.postMediaGallery && window.ironman.postMediaGallery[galleryKey]) || [];
    gallery.media = Array.isArray(src) ? src.slice() : [];
    gallery.total = (window.ironman.postMediaGalleryCounts && window.ironman.postMediaGalleryCounts[galleryKey]) || 0;
    gallery.allLoaded = gallery.total ? (gallery.media.length >= gallery.total) : false;
    
    return gallery;
}

/**
 * Return the current gallery item index.
 * Note: Use this to get the current index when updating the gallery with new items. This way it gets the value in real time, rather than when the callback is called.
 * @returns number
 */
export function getCurrentGalleryItemIndex() {
    return currentGalleryItemIndex;
}

export function getCurrentGalleryKey() {
    return currentGalleryKey;
}
window.getCurrentGalleryKey = getCurrentGalleryKey;
window.getCurrentGalleryItemIndex = getCurrentGalleryItemIndex;

/* =========================================================
   LightGallery Mapping
   ========================================================= */

function escapeHtml(str) {
    return String(str || "").replace(/[&<>"']/g, function (c) {
        return {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;"
        }[c];
    });
}

/**
 * Convert an item from galleryStates to format needed by LightGallery
 * @param {*} m 
 * @returns 
 */
function toLgItem(m) {
    const isUploader = window.ironman.userData.loggedInUser && parseInt(m.uploader_id) === window.ironman.userData.userId;

    // Build the caption HTML.
    let captionHtml = "<div class='lg-caption' data-media-id='"+m.id+"'>";
    if(m.caption) {
        captionHtml += "<p><span data-label='captionText'>" + escapeHtml(m.caption) + "</span>";
        if(isUploader) {
            captionHtml += "<a class='lg-caption-edit ms-2' data-action='openEditCaptionPopup'>"+t('editComment')+"</a>";
        }
        captionHtml += "</p>";
    } else {
        if(isUploader) {
            captionHtml += "<a class='lg-caption-add' data-action='openEditCaptionPopup'>"+t('writeComment')+"</a>";
        }
    }
    captionHtml += "</div>";
    
    const subHtml = "<div class='lg-uploader-image'><a href='"+m.uploader_profile_url+"'><img src='"+m.uploader_avatar+"' alt='"+m.uploader_name+"' /></a></div><div class='lg-uploader-userlink'><a href='"+m.uploader_profile_url+"'>"+m.uploader_name+"</a></div>"+captionHtml+"</div>";

    // If the media type is a video, return the video item.
    if (m.media_type === "video") {
        if(m.media_url) {
            return {
                thumb: m.media_url_thumbnail,
                subHtml: subHtml,
                alt: 'video_',
                video: {
                    source: [{ src: m.media_url, type: "video/mp4" }],
                    attributes: {
                        controls: true,
                        playsinline: true,
                        preload: "metadata",
                        loop: true,
                        muted: true,
                        autoplay: true,
                        download: false,
                        poster: m.media_url_thumbnail,
                    }
                }
            };
        } else {
            // This is a video that is still processing. Return a placeholder item.
            return {
                src: m.media_url_thumbnail,
                thumb: m.media_url_thumbnail,
                subHtml: '<div class="lg-video-processing">'+t('videoProcessing')+'</div>',
                alt: 'video_',
            };
        }
    }

    // Otherwise must be an image.
    return {
        src: m.media_url,
        thumb: m.media_url_thumbnail,
        subHtml: subHtml
    };
}

/**
 * Loop through a state object and convert each .media item to a LG item, and output the array of items used by LG.
 * @param {*} state 
 * @returns 
 */
function buildDynamicEl(state) {

    // Idea. maybe just add placeholders to the return array here, rather than in the state.media object.
    // This would make it easier to add placeholders to the gallery without having to modify the state.media object.
    // Otherwise need to update everywhere media.length is used.
    const realItems = state.media.map(toLgItem);

    // Return here with real items, OR continue to create placeholder items and return the combined array.
    
    const totalCount = window.ironman.postMediaGalleryCounts[state.key];
    const placeholderCount = totalCount - realItems.length;

    const px = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
    
    let placeholderItems = [];

    // Create N placeholder items.
    placeholderItems = Array(placeholderCount).fill({
        src: px,
        thumb: px,
        subHtml: '<div class="lg-caption-skeleton"><div class="lg-caption-line"></div><div class="lg-caption-line short"></div></div>',
        alt: 'Placeholder_'
    });
    return realItems.concat(placeholderItems);
}

/* =========================================================
   AJAX Load More (WordPress admin-ajax)
   ========================================================= */

function fetchMoreItems(state) {
    if (state.loading || state.allLoaded) {
        return $.Deferred().resolve([]).promise();
    }

    state.loading = true;

    const stateKey = normalizeGalleryKey(state.key || 'root');
    
    const data = {
        post_id: window.ironman.postId,
        child_id: stateKey,
        offset: state.media.length, // Todo, use a different way to calculate the offset once we have placeholder media.
        limit: GALLERY_CONFIG.PAGE_SIZE,
    };

    callAdminAjax('querySinglePostMediaGallery', data, 'querySinglePostMediaGallery_'+stateKey, {
        onSuccess: (response) => {
            if(response.success === true) {
                log('Post media gallery updated successfully.');

                // Safely append the new items to the existing items in the window.ironman.postMediaGallery.
                log('fetchMoreItems: Appending new items to existing items', window.ironman.postMediaGallery[stateKey], response.data.query_results);
                window.ironman.postMediaGallery[stateKey] = [
                    ...(window.ironman.postMediaGallery[stateKey] || []),
                    ...response.data.query_results
                ];

                // Dedupe in case the order switched between fetches
                window.ironman.postMediaGallery[stateKey] = window.ironman.postMediaGallery[stateKey].filter((media, index, self) =>
                    index === self.findIndex((t) => t.id === media.id)
                );

                // Update the allLoaded flag.
                state.allLoaded = window.ironman.postMediaGallery[stateKey].length >= window.ironman.postMediaGalleryCounts[stateKey];

                // Refresh the gallery with the new items.
                refreshGallery(stateKey, false);
                
            } else {
                log('error getting post media gallery items.');
            }
        },
        onFail: (error) => {
            log('error getting post media gallery.');
        },
        onComplete: (response) => {
            state.loading = false;
        }
    }, 'non-blocking', 'Downloading media items...');
}


/* =========================================================
   Overlay UI
   ========================================================= */

function buildOverlay(galleryType, total) {
    var dashes = "";
    if(total>1) {
        // Don't include top dashes if it's less than 2.
        for (var i = 0; i < total; i++) {
            dashes += '<span class="lg-reels-dash"></span>';
        }
    }

    let attach_link = '';
    if(galleryType === "singleStage") {
        if(['root',null].includes(getCurrentGalleryKey())) {
            // Only show attach link on Single Itinerary page
            if(window.ironman.stageData && Object.keys(window.ironman.stageData).length > 0) {
                attach_link = '<li><a class="dropdown-item postOwnerOnly" data-action="openAttachStagePopup">'+t('attachToStage')+'</a></li>';
            }
        } else {
            attach_link = '<li><a class="dropdown-item postOwnerOnly" data-action="updateMediaChild" data-new-child-id="null">'+t('unattachFromStage')+'</a></li>';
        }
    }

    let overlay =
        '<div class="lg-reels-ui">' +
            '<div class="lg-reels-top">' +
                '<div class="lg-reels-dashes">' + dashes + '</div>' +
            '</div>';

            // '<button class="lg-reels-menu-btn">⋯</button>' +
            // '<div class="lg-reels-menu">' +
            //     '<button data-action="delete">Delete image</button>' +
            //     '<button data-action="report">Report photo</button>' +
            //     '<button data-action="download">Download</button>' +
            // '</div>' +

    if(window.ironman.userData.loggedInUser) {
        overlay +=
            '<div class="lg-reels-actions">' +
                '<button class="lg-reels-action no-animate" data-action="like">' +
                    '<div class="icon">' +
                        '<svg class="heart-icon" viewBox="0 0 32 32">' +
                            '<path d="M16 28.5L14.1 26.7C7.3 20.5 2.8 16.5 2.8 11.5 2.8 7.4 6 4.2 10.1 4.2c2.3 0 4.5 1.1 5.9 2.8 1.4-1.7 3.6-2.8 5.9-2.8 4.1 0 7.3 3.2 7.3 7.3 0 5-4.5 9-11.3 15.2L16 28.5z" />' +
                        '</svg>' +
                    '</div>' +
                    '<div class="label" data-field="likeCount">0</div>' +
                '</button>';

        if(galleryType === "singleStage") {
            overlay += 
            '<button class="lg-reels-action" data-action="reshare">' +
                '<div class="icon">+</div>' +
                '<div class="label">'+t('addToCamino')+'</div>' +
            '</button>';
        }
        overlay +=
            '<button class="lg-reels-action postOwnerOnly makePrimaryButton" data-action="makePrimary">' +
                '<div class="icon">⤴︎</div>' +
                '<div class="label">'+t('makePrimary')+'</div>' +
            '</button>' +
            // '<button class="lg-reels-action postOwnerOnly" data-action="moveUp">' +
            //     '<div class="icon">⬆︎</div>' +
            //     '<div class="label">Move Up</div>' +
            // '</button>' +
            // '<button class="lg-reels-action postOwnerOnly" data-action="moveDown">' +
            //     '<div class="icon">⬇︎</div>' +
            //     '<div class="label">Move Down</div>' +
            // '</button>' +
            // '<button class="lg-reels-action" data-action="editCaption">' +
            //     '<div class="icon">P</div>' +
            //     '<div class="count">Edit Caption</div>' +
            // '</button>' +
            // '<div class="lg-reels-dropdown">' +
            //     '<button class="lg-reels-action dropdown-toggle" data-action="moreOptions" type="button" data-bs-toggle="dropdown">' +
            //         '<div class="icon">...</div>' +
            //     '</button>' +
            //     '<ul class="dropdown-menu">' +
            //         '<li><a class="dropdown-item" data-action="delete">Delete image</a></li>' +
            //         '<li><a class="dropdown-item" href="#">Report Image</a></li>' +
            //         '<li><a class="dropdown-item" href="#">Something else here</a></li>' +
            //     '</ul>' +
            // '</div>' +
            // '<button class="lg-reels-action" data-action="comment">' +
            //     '<div class="icon">💬</div>' +
            //     '<div class="count" data-field="commentCount">0</div>' +
            // '</button>' +
        '</div>' +

        '<div class="lg-reels-dropdown">' +
            '<button class="lg-reels-action dropdown-toggle" data-action="moreOptions" type="button" data-bs-toggle="dropdown">' +
                '<div class="icon">...</div>' +
            '</button>' +
            '<ul class="dropdown-menu">' +
                '<li><a class="dropdown-item postOwnerOnly" data-action="moveUp">Move Up</a></li>' +
                '<li><a class="dropdown-item postOwnerOnly" data-action="moveDown">Move Down</a></li>' +
                attach_link +
                '<li><a class="dropdown-item removeImageButton redText" data-action="deleteImage">Remove Media</a></li>' +
                '<li><a class="dropdown-item deleteImageButton redText" data-action="deleteImage">Delete Media</a></li>' +
                '<li><a class="dropdown-item darkText" data-action="reportMediaItem">Report Media</a></li>' +
            '</ul>' +
        '</div>' +

        '</div>'; // close lg-reels-actions div
    }
    overlay +=
        '<div class="lg-reels-slide-counter">' +
            '<span class="text" data-field="slideCounter"></span>' +
            '/'+
            '<span class="text" data-field="slideCounterTotal">'+String(total)+'</span>' +
        '</div>';
    
    return $(overlay);
}

function updateOverlay(state, index) {
    if (!state.$ui) return;

    var m = state.media[index];
    // If the item isn't found, close the gallery.
    if(!m) {
        log('updateOverlay: Item not found, closing gallery');
        destroyGallery(state);
        return;
    }


    currentGalleryItemIndex = index;
    log('updateOverlay: Current gallery item index', currentGalleryItemIndex);

    // Set media-id and gallery key attributes on .lg-outer for use by click handlers.
    state.$ui.closest('.lg-outer').attr("data-media-id", parseInt(m.id)).attr("data-gallery-key", state.key);

    state.$ui.find(".lg-reels-dash").each(function (i) {
        $(this).toggleClass("active", i <= index);
    });

    // Update counter
    state.$ui.find('[data-field="slideCounter"]').text(String(index+1));

    // Show/Hide postOwnerOnly actions.
    const isPostOwner = window.ironman.userData.loggedInUser && window.ironman.postOwnerId && window.ironman.postOwnerId === window.ironman.userData.userId;
    state.$ui.find(".postOwnerOnly").toggle(isPostOwner);
    const isAdmin = window.ironman.userData.loggedInUser && window.ironman.userData.isAdmin === true || false;

    // Update overlay values
    if(m) {
        state.$ui.find('[data-field="likeCount"]').text(
            (parseInt(m.like_count, 10) || 0).toLocaleString()
        );
        
        // Update like button state
        state.$ui.find("[data-action='like']").toggleClass("liked", isLikedByUser(parseInt(m.id)));

        // Show/Hide uploaderOnly actions.
        const isUploader = window.ironman.userData.loggedInUser && parseInt(m.uploader_id) === window.ironman.userData.userId;
        state.$ui.find(".uploaderOnly").toggle(isUploader);

        // Enable/Disable makePrimaryButton.
        if(m.manual_rank === '1') {
            state.$ui.find(".makePrimaryButton").prop("disabled", true);
        } else {
            state.$ui.find(".makePrimaryButton").prop("disabled", false);
        }

        // Show/Hide deleteImage button.

        // Hide both deleteImage and removeImage buttons.
        state.$ui.find(".removeImageButton").hide();
        state.$ui.find(".deleteImageButton").hide();

        const postType = window.ironman.postType;

        // Mimic the logic in the PHP function: ($mission === 'remove').
        if(isPostOwner || isUploader) {
            if(window.ironman.appConfig.mediaGalleryPostTypesOnlyUploaderCanRemove.includes(postType)) {
                if(isUploader || isAdmin) {
                    //continue
                } else {
                    return;
                }
            } else {
                // Post owner OR uploader can remove.
                if(isPostOwner || isUploader || isAdmin) {
                    //continue
                } else {
                    return;
                }
            }

            if(isUploader && m.source_id === null) {
                //Can delete the image
                state.$ui.find(".deleteImageButton").show();
            } else {
                //Can only remove the image
                state.$ui.find(".removeImageButton").show();
            }
        }
    } else {
        // Must be a placeholder slide.
        state.$ui.find('[data-field="likeCount"]').text("0");
        state.$ui.find("[data-action='like']").removeClass("liked");
        state.$ui.find(".uploaderOnly").hide();
        state.$ui.find(".makePrimaryButton").prop("disabled", true);
    }
}

function scrollToActiveThumbnail(state) {
    log('scrollToActiveThumbnail: Scrolling to active thumbnail', state);
    if (!state.$ui) return;

    const activeThumbnail = state.$ui.parent().find(".lg-thumb-outer").find(".lg-thumb-item.active");
    if (activeThumbnail.length > 0) {
        activeThumbnail.get(0).scrollIntoView({ behavior: "instant" });
    }
}

/* =========================================================
   Gallery Open / Rebuild
   ========================================================= */

function openGallery(state, galleryType, startIndex) {

    log('openGallery: Opening gallery', state, galleryType, startIndex);

    state.lgContainer = document.createElement("div");

    state.lgContainer.addEventListener("lgInit", function (ev) {
        log('openGallery: Gallery initialized', ev);
        state.lgInstance = ev.detail.instance;
    });

    state.lgContainer.addEventListener("lgAfterOpen", function (ev) {
        log('openGallery: LightGallery state', state);
    
        // Build overlay
        state.$ui = buildOverlay(galleryType, state.total || state.media.length);
    
        // Append to the correct place (root of lightbox)
        var outerEl = state.lgInstance.outer.get(); // DOM element

        // Add the slide count and class to the outer element.
        $(outerEl).attr('data-total-slide-count', state.total || state.media.length);
        if(state.total > 1) {
            $(outerEl).addClass('has-multiple-slides');
        } else {
            $(outerEl).removeClass('has-multiple-slides');
        }
        
        $(outerEl).append(state.$ui);

        // log('openGallery: Outer element', outerEl);
        // log('openGallery: State $ui', state.$ui);
    
        // Debug: confirm it's there
        // log("overlay appended?", $(outerEl).find(".lg-reels-ui").length);
    
        // Update contents
        updateOverlay(state, state.lgInstance.index);

        // Scroll to active thumbnail.
        scrollToActiveThumbnail(state);
    });

    state.lgContainer.addEventListener("lgAfterSlide", function (ev) {

        var index = ev.detail.index;
        updateOverlay(state, index);

        // Scroll to active thumbnail.
        scrollToActiveThumbnail(state);

        // Maybe fetch more slides
        if (!state.allLoaded && !state.loading && (index >= state.media.length - GALLERY_CONFIG.LOAD_MORE_THRESHOLD)) {
            fetchMoreItems(state);
        }
    });

    state.lgContainer.addEventListener("lgAfterClose", function () {
        destroyGallery(state);
    });

    const lgInstance = lightGallery(state.lgContainer, {
        controls: true,
        showControls: true,
        counter: false,
        dynamic: true,
        dynamicEl: buildDynamicEl(state),
        index: startIndex,
        plugins: [lgVideo, lgThumbnail],
        licenseKey: window.ironman.appConfig.lightgalleryjsLicense,
        download: false,
        preload: 4,
        speed: 0,
        loop: false,
        addClass: 'lg-reels-container',
        appendThumbnailsTo: '.lg-outer',
        animateThumb: false,
        allowMediaOverlap: false,
        thumbWidth: 250,
        zoomFromOrigin: false,
        startAnimationDuration: 0,
    });
    log('openGallery: LightGallery instance', lgInstance);

    state.lgInstance.openGallery(startIndex);
}

function destroyGallery(state) {
    if (state.lgInstance) {
        try {
            state.lgInstance.destroy(true);
            log('destroyGallery: LightGallery instance destroyed');
        } catch (e) {}
    }

    if (state.$ui) {
        state.$ui.remove();
    }

    state.lgInstance = null;
    state.lgContainer = null;
    state.$ui = null;

    currentGalleryKey = null;
    currentGalleryItemIndex = null;
}

export function refreshGallery(galleryKey, refresh_only=false, newIndex=null) {
    log('refreshGallery: Refreshing gallery', galleryKey, refresh_only);
    // Update the gallery with the new items.
    const gallery = getState(galleryKey);
    
    if(newIndex === null) {
        newIndex = getCurrentGalleryItemIndex();
    }

    if (gallery.lgInstance) {
        const dynamicGalleryItems = buildDynamicEl(gallery);
        if(refresh_only) {
            //refresh the gallery to refresh the items.
            gallery.lgInstance.refresh(dynamicGalleryItems);
        } else {
            //update the gallery to replace placeholder slides with real slides.
            gallery.lgInstance.updateSlides(dynamicGalleryItems, newIndex);
            // If the current item is one that just changed, it might not consider it valid to show. So go to it again.
            requestAnimationFrame(() => {
                gallery.lgInstance.slide(newIndex, false, false);
            });
        }
    }
}


/* =========================================================
   Thumbnail Click Binding
   ========================================================= */

function bindThumbnailClicks() {
    log('bindThumbnailClicks: Binding thumbnail clicks');
    $(document).on("click", "[data-post-media-gallery='true'] [data-post-media-gallery-thumb='true']", function (e) {
        log('bindThumbnailClicks: Clicked on preview item', $(this));
        e.preventDefault();

        // Confirm the data-post-media-gallery-thumb='true' attribute hasn't been changed to false since the click event was bound.
        if($(this).attr("data-post-media-gallery-thumb") !== "true") {
            log('bindThumbnailClicks: Data-post-media-gallery-thumb attribute has been changed to false since the click event was bound');
            return false;
        }

        const $container = $(this).closest("[data-post-media-gallery='true']");
        const galleryKey = String($container.data("gallery-key") || "root");
        const galleryType = String($container.data("gallery-type") || "default");
        const startIndex = parseInt($(this).data("index"), 10) || 0;

        // Confirm the gallery actually has items. (E.g. In case of placeholder item click)
        if(!window.ironman.postMediaGallery[galleryKey] || window.ironman.postMediaGallery[galleryKey]?.length === 0) {
            log('bindThumbnailClicks: Gallery has no items');
            return false;
        }

        // Create the state for the gallery if it doesn't exist.
        const state = getState(galleryKey);

        // Set the global gallery key.
        currentGalleryKey = galleryKey;

        // Open the gallery.
        openGallery(state, galleryType, startIndex);
    });
}

/* =========================================================
    Overlay Click Handlers
    ========================================================= */

function bindOverlayClicks() {
    // Like button click handler.
    // Note: No need to validate that the user hasn't already liked the item. It will just fail it liked again. Good users won't be able to click the like button if they've already liked it.
    $(document).on("click", ".lg-reels-actions [data-action=like]", function (e) {
        log('bindOverlayClicks: Clicked on like button', $(this));
        e.preventDefault();

        const $button = $(this);
        if(currentGalleryItemIndex === null || currentGalleryItemIndex === undefined) {
            log('bindOverlayClicks: Missing gallery item index');
            return false;
        }

        const galleryItem = window.ironman.postMediaGallery[currentGalleryKey][currentGalleryItemIndex];
        const galleryItemId = parseInt(galleryItem.id);

        // Check if this is a like or an unlike.
        const isLiked = isLikedByUser(galleryItemId);
        // log('bindOverlayClicks: Is liked', isLiked, galleryItemId, galleryItem);

        if(isLiked) {
            updatePostMediaGalleryItem(galleryItemId, "unlike");
            // Assume success and update the like count in the gallery item.
            galleryItem.like_count = parseInt(galleryItem.like_count || 0) - 1;
            // Update the local set of user likes.
            removeUserLike(galleryItemId);
        } else {
            updatePostMediaGalleryItem(galleryItemId, "like");
            // Assume success and update the like count in the gallery item.
            galleryItem.like_count = parseInt(galleryItem.like_count || 0) + 1;
            // Update the local set of user likes.
            addUserLike(galleryItemId);
        }

        // Delay and update the UI to reflect the new like count and button state.
        setTimeout(() => {
            const likeCount = galleryItem.like_count || 0;
            log('bindOverlayClicks: Updating like count to', likeCount);
            $button.find(".label").text(String(likeCount));

            // Update like button state to the inverse of the original state.
            // Remove the no-animate class first - this is here to block transitions during the slide change.
            $button.removeClass("no-animate");
            $button.toggleClass("liked", !isLiked);
            setTimeout(() => {
                $button.addClass("no-animate");
            }, 600);
        }, 100);
    });

    $(document).on("click", ".lg-reels-actions [data-action=reshare]", function (e) {
        log('bindOverlayClicks: Clicked on reshare button', $(this));
        e.preventDefault();

        const $button = $(this);
        if(currentGalleryItemIndex === null || currentGalleryItemIndex === undefined) {
            log('bindOverlayClicks: Missing gallery item index');
            return false;
        }
    });

    $(document).on("click", ".lg-reels-actions [data-action=makePrimary]", async function (e) {

        log('bindOverlayClicks: Clicked on make primary button', $(this));
        e.preventDefault();

        const $button = $(this);
        if(currentGalleryItemIndex === null || currentGalleryItemIndex === undefined) {
            log('bindOverlayClicks: Missing gallery item index');
            return false;
        }
        const galleryItem = window.ironman.postMediaGallery[currentGalleryKey][currentGalleryItemIndex];
        const galleryItemId = parseInt(galleryItem.id);

        const response = await updatePostMediaGalleryItem(galleryItemId, "set_primary");
        if(response.success) {
            // Update the post media gallery in the UI from the updated window.ironman.postMediaGallery.
            window.ironman.postMediaGallery = response.data.post_media_gallery;
            window.ironman.postMediaGalleryCounts = response.data.post_media_gallery_counts;
            renderMediaGallery(currentGalleryKey);
            refreshGallery(currentGalleryKey, false, 0);
        } else {
            log('bindOverlayClicks: Error making primary', response.error);
        }

        //wait 200ms and then update the UI to reflect the new primary status.
        setTimeout(() => {
            $button.prop("disabled", true);
        }, 200);
    });

    $(document).on("click", ".lg-reels-dropdown [data-action=deleteImage]", async function (e) {

        log('bindOverlayClicks: Clicked on delete button', $(this));
        e.preventDefault();

        // Validate the gallery item index.
        if(currentGalleryItemIndex === null || currentGalleryItemIndex === undefined) {
            log('bindOverlayClicks: Missing gallery item index');
            return false;
        }
        const galleryItem = window.ironman.postMediaGallery[currentGalleryKey][currentGalleryItemIndex];
        const galleryItemId = parseInt(galleryItem.id);

        // Guard against multiple clicks.
        if (inFlightActions.deleteImage.includes(galleryItemId)) {
            log('bindOverlayClicks: Deleting already in progress for this item');
            return;
        }
        // Add the item to the in flight actions.
        inFlightActions.deleteImage.push(galleryItemId);

        try {
            // Call the async function to update the post media gallery item.
            const response = await updatePostMediaGalleryItem(galleryItemId, "remove");
            if(response.success) {
                // Remove the item from the gallery.
                window.ironman.postMediaGallery[currentGalleryKey].splice(currentGalleryItemIndex, 1);
                //remove the item from the post media gallery counts
                window.ironman.postMediaGalleryCounts[currentGalleryKey]--;
                //refresh the gallery to remove the item
                log('refreshing gallery to remove item', currentGalleryKey);
                refreshGallery(currentGalleryKey, false);

                // Render the gallery again to update the UI.
                renderMediaGallery(currentGalleryKey);
            } else {
                log('bindOverlayClicks: Error deleting image', response.error);
            }
        } catch (error) {
            log('bindOverlayClicks: Error deleting image', error);
        } finally {
            // Remove the item from the in flight actions.
            inFlightActions.deleteImage = inFlightActions.deleteImage.filter(id => id !== galleryItemId);
        }
    });

    $(document).on("click", ".lg-reels-dropdown [data-action=moveUp]", function (e) {

        log('bindOverlayClicks: Clicked on make primary button', $(this));
        e.preventDefault();

        const $button = $(this);
        if(currentGalleryItemIndex === null || currentGalleryItemIndex === undefined) {
            log('bindOverlayClicks: Missing gallery item index');
            return false;
        }
    });

    $(document).on("click", ".lg-reels-dropdown [data-action=moveDown]", function (e) {

        log('bindOverlayClicks: Clicked on make primary button', $(this));
        e.preventDefault();

        const $button = $(this);
        if(currentGalleryItemIndex === null || currentGalleryItemIndex === undefined) {
            log('bindOverlayClicks: Missing gallery item index');
            return false;
        }
    });

    $(document).on("click", "a[data-action='updateMediaChild']", async function (e) {

        log('bindOverlayClicks: Clicked on update media child button', $(this));
        e.preventDefault();

        // Validate the gallery item index.
        if(currentGalleryItemIndex === null || currentGalleryItemIndex === undefined) {
            log('bindOverlayClicks: Missing gallery item index');
            return false;
        }
        const galleryItem = window.ironman.postMediaGallery[currentGalleryKey][currentGalleryItemIndex];
        const galleryItemId = parseInt(galleryItem.id);
        let newChildId = $(this).data("new-child-id");
        if(newChildId === null || newChildId === 'null') {
            newChildId = false;
        } else {
            newChildId = parseInt(newChildId);
            if(isNaN(newChildId)) {
                log('bindOverlayClicks: Missing new child id');
                return false;
            }
        }

        // Guard against multiple clicks.
        if (inFlightActions.updateMediaChild.includes(galleryItemId)) {
            log('bindOverlayClicks: Updating media child already in progress for this item');
            return;
        }
        // Add the item to the in flight actions.
        inFlightActions.updateMediaChild.push(galleryItemId);

        try {
            // Call the async function to update the post media gallery item.
            const response = await updatePostMediaGalleryItem(galleryItemId, "update_child", newChildId);
            if(response.success) {
                // Remove the item from the gallery.
                window.ironman.postMediaGallery[currentGalleryKey].splice(currentGalleryItemIndex, 1);
                //remove the item from the post media gallery counts
                window.ironman.postMediaGalleryCounts[currentGalleryKey]--;
                //refresh the gallery to remove the item
                log('refreshing gallery to remove item', currentGalleryKey);
                refreshGallery(currentGalleryKey, false);

                // Render the gallery again to update the UI.
                renderMediaGallery(currentGalleryKey);

                // Close the action menu modal (if open).
                closeFenixModal();
            } else {
                log('bindOverlayClicks: Error updating media child', response.error);
            }
        } catch (error) {
            log('bindOverlayClicks: Error updating media child', error);
        } finally {
            // Remove the item from the in flight actions.
            inFlightActions.updateMediaChild = inFlightActions.updateMediaChild.filter(id => id !== galleryItemId);
        }
    });

    $(document).on('click', '[data-action="openAttachStagePopup"]', function(e) {
        e.preventDefault();

        // Get the available stages for the current post.
        const stages = window.ironman.stageData;
        const items = [];
        let index = 1;
        stages.forEach(stage => {
            items.push({
                icon: 'ci-arrow-right-line',
                text: 'Stage ' + index,
                action: 'updateMediaChild',
                data: { 'new-child-id': stage.id.toString() }
            });
            index++;
        });
        showActionMenuModal('Attach to Stage', items);
    });

    // $(document).on("click", ".lg-reels-actions [data-action=moreOptions]", function (e) {
    //     log('bindOverlayClicks: Clicked on more options button', $(this));
        
    //     const mq = window.matchMedia('(min-width: 768px)');

    //     if(mq.matches) {
    //         log('bindOverlayClicks: More options button clicked on desktop');
    //         // Continue with the default bootstrap dropdown behavior.
    //         return;
    //     } else {
    //         log('bindOverlayClicks: More options button clicked on mobile');
    //         e.preventDefault();
    //         e.stopPropagation();
    //         // dropdown.hide();
    //         // openActionsModal();
    //     }
    // });

    $(document).on("click", "[data-action=openEditCaptionPopup]", function (event) {
        event.preventDefault();
        const $button = $(this);
        const galleryItemId = parseInt($button.closest('.lg-outer').attr("data-media-id"));
        const galleryKey = $button.closest('.lg-outer').attr("data-gallery-key");
        showEditCaptionPopup(galleryItemId, galleryKey);
    });

    $(document).on("click", "[data-action=editMediaCaptionModal_submit]", async function (event) {
        event.preventDefault();
        const $button = $(this);
        const galleryItemId = parseInt($button.attr("data-media-id"));
        const comment = $('#editMediaCaptionContainer textarea[data-label="editMediaCaptionTextarea"]').val();
        log('editMediaCaptionModal_submit: Comment', comment);
        // if(comment) {
            const response = await updatePostMediaGalleryItem(galleryItemId, "add_caption", comment);
            if(response.success) {
                log('editMediaCaptionModal_submit: Success', response.data);
                // Update the gallery item in window.ironman.postMediaGallery
                // Find the gallery item index.
                const key = getCurrentGalleryKey();
                // log('editMediaCaptionModal_submit: Current gallery key and item id', key, galleryItemId);
                const galleryItemIndex = window.ironman.postMediaGallery[key].findIndex(item => parseInt(item.id) === parseInt(galleryItemId));
                // log('editMediaCaptionModal_submit: Gallery item index', galleryItemIndex);
                if(galleryItemIndex > -1) {
                    window.ironman.postMediaGallery[key][galleryItemIndex].caption = comment.substring(0, 300);
                    // log('editMediaCaptionModal_submit: Updated gallery item caption', window.ironman.postMediaGallery[key][galleryItemIndex].caption);
                }
                // Update the caption text in the UI
                $(document).find('.lg-reels-container .lg-outer .lg-components .lg-sub-html .lg-caption').find('[data-label="captionText"]').text(comment.substring(0, 300));
                closeFenixModal();
            } else {
                log('editMediaCaptionModal_submit: Error', response.error);
            }
        // }
    });

    $(document).on("click", "[data-action=reportMediaItem]", function (event) {
        event.preventDefault();
        const $button = $(this);
        const galleryItemId = parseInt($button.closest('.lg-outer').attr("data-media-id"));
        showReportMediaItemPopup(galleryItemId);
    });

    $(document).on("click", "[data-action=reportMediaItemModal_submit]", async function (event) {
        event.preventDefault();
        const $button = $(this);
        const galleryItemId = parseInt($button.attr("data-media-id"));
        const reason = $('#reportMediaItemContainer select[id="reportMediaItemModalReason"] option:selected').val();   
        const comment = $('#reportMediaItemContainer textarea[data-label="reportMediaItemModalTextarea"]').val();
        if(comment && comment.length > 5) {
            const response = await createMediaItemReport(galleryItemId, reason, comment);
            if(response.success) {
                log('editMediaCaptionModal_submit: Success', response.data);
                // Update the gallery item in window.ironman.postMediaGallery
                // Find the gallery item index.
                const key = getCurrentGalleryKey();
                // log('editMediaCaptionModal_submit: Current gallery key and item id', key, galleryItemId);
                const galleryItemIndex = window.ironman.postMediaGallery[key].findIndex(item => parseInt(item.id) === parseInt(galleryItemId));
                // log('editMediaCaptionModal_submit: Gallery item index', galleryItemIndex);
                if(galleryItemIndex > -1) {
                    window.ironman.postMediaGallery[key][galleryItemIndex].caption = comment.substring(0, 300);
                    // log('editMediaCaptionModal_submit: Updated gallery item caption', window.ironman.postMediaGallery[key][galleryItemIndex].caption);
                }
                // Update the caption text in the UI
                $(document).find('.lg-reels-container .lg-outer .lg-components .lg-sub-html .lg-caption').find('[data-label="captionText"]').text(comment.substring(0, 300));
                closeFenixModal();
            } else {
                log('editMediaCaptionModal_submit: Error', response.error);
            }
        } else {
            log('reportMediaItemModal_submit: Error', 'No comment provided');
            $('#reportMediaItemModalExplanationWarning').removeClass('d-none');
        }
    });
}

// Mimic on pageload php gallery rendering behavior, but in js.
export function renderMediaGallery(galleryKey) {
    log('renderMediaGallery: Rendering gallery', galleryKey);
    if(!galleryKey || galleryKey === 'root') {
        galleryKey = 'root';
    } else {
        galleryKey = parseInt(galleryKey);
    }
    log('renderMediaGallery: Gallery key', galleryKey);

    // Find all media gallery containers on page that match the gallery key.
    const $containers = $(document).find("[data-post-media-gallery='true'][data-gallery-key='" + galleryKey + "']");

    log('renderMediaGallery: Matching containers', $containers.length);
    if($containers.length > 0) {
        // Loop through each container and render the gallery.
        $containers.each(function() {
            const $container = $(this);
            log('renderMediaGallery: Container', $container);

            // Use window.ironman.postMediaGallery and window.ironman.postMediaGalleryCounts to render the gallery.
            const placement = $container.data("media-gallery-placement");
            log('renderMediaGallery: Placement', placement);

            const new_media = [];

            if(placement === "mediaGallery_directoryPageHeader") {
                // Directory Gallery Template
                const mediaGallery = window.ironman.postMediaGallery[galleryKey];
                const mediaGalleryCount = window.ironman.postMediaGalleryCounts[galleryKey];
                log(mediaGallery);
                log(mediaGallery.length);
                
                // First item is hardcoded. Items indexed 1-4 are dynamic.
                if(mediaGallery && mediaGallery.length > 0) {
                    log('renderMediaGallery: Media gallery', mediaGallery, mediaGallery.length);
                    // Featured item
                    const featuredItem = mediaGallery[0];
                    $container.find("[data-label='featured-media-item-link']").attr('data-post-media-gallery-thumb', 'true');
                    $container.find("[data-label='featured-media-item-thumbnail']").attr("src", featuredItem.media_url_thumbnail).attr('alt', featuredItem.caption || '');
                    $container.find("[data-label='total-media-gallery-item-count']").text(mediaGalleryCount);
                    // $container.find("[data-label='total-media-gallery-item-count-button']").removeClass("d-none");

                    // Handle the secondary items.
                    if(mediaGallery.length > 1) {
                        const $template = getTemplate('mediaGallery_directoryPageHeaderSingleItemTemplate');
                        mediaGallery.slice(1, 5).forEach((mediaItem, index) => {
                            const $item = $template.clone();
                            $item.find("[data-label='media-item-link']").attr("data-index", index + 1);
                            $item.find("[data-label='media-item-thumbnail']").attr("src", mediaItem.media_url_thumbnail).attr('alt', mediaItem.caption || '');
                            if(index === 3 && mediaGalleryCount > 5) {
                                $item.find("[data-label='media-item-overlay']").remove();
                                $item.find("[data-label='media-item-final-overlay-count']").text(mediaGalleryCount - 5);
                                $item.find("[data-label='media-item-final-overlay']").removeClass("d-none");
                            } else {
                                $item.find("[data-label='media-item-final-overlay']").remove();
                            }
                            new_media.push($item);
                        });
                    }

                    // Add the new media items to the container.
                    $container.append(new_media);
                } else {
                    // No featured item
                    log('renderMediaGallery: No featured item');

                    // Disable the lightgallery link
                    $container.find("[data-label='featured-media-item-link']").attr('data-post-media-gallery-thumb', 'false');
                    // Blank the thumbnail
                    $container.find("[data-label='featured-media-item-thumbnail']").attr('src', window.ironman.appConfig.emptyGalleryImageUrl);
                    // Hide the count button
                    // $container.find("[data-label='total-media-gallery-item-count-button']").addClass("d-none");
                    // Zero the count 
                    $container.find("[data-label='total-media-gallery-item-count']").text('0');
                }
                // Clear existing media items and append the new items.
                $container.find("[data-label='secondary-media-gallery-items']").empty();
                if(new_media.length > 0) {
                    $container.find("[data-label='secondary-media-gallery-items']").append(new_media);
                }

            } else if(placement === "mediaGallery_directoryPageHeader_swiper") {
                // Directory Page Header Swiper Template
                const mediaGallery = window.ironman.postMediaGallery[galleryKey];
                const mediaGalleryCount = window.ironman.postMediaGalleryCounts[galleryKey];
                log(mediaGallery);
                log(mediaGallery.length);
                
                // Clear existing swiper items.
                $container.find("[data-label='media-gallery-swiper-items']").empty();
                
                // Update the total media gallery item count.
                $container.find("[data-label='total-media-gallery-item-count']").text(mediaGalleryCount);
                // Get the template for the swiper items.
                const $template = getTemplate('mediaGallery_directoryPageHeader_swiperItemTemplate');
                
                // Loop through each media item and add it to the container.
                mediaGallery.forEach((mediaItem, index) => {
                    const $item = $template.clone();
                    $item.find("[data-label='media-item-link']").attr("data-index", index);
                    $item.find("[data-label='media-item-thumbnail']").attr("src", mediaItem.media_url_thumbnail).attr('alt', mediaItem.caption || '');
                    new_media.push($item);
                });
                $container.find("[data-label='media-gallery-swiper-items']").append(new_media);

                // Update the swiper instance.
                const swiperInstance = getDirectoryRootMediaGallery_swiperInstance();
                if(swiperInstance) {
                    log('renderMediaGallery: Updating swiper instance', swiperInstance);
                    swiperInstance.update();
                }
            } else if(placement === "mediaGallery_singleStageItem") {
                // Single Stage Item (And Root Album) Template

                // Add new media items (limit to 4)
                const mediaGallery = window.ironman.postMediaGallery[galleryKey];
                log('renderMediaGallery: Media gallery', mediaGallery, galleryKey);
                if(mediaGallery) {
                    const $template = getTemplate('mediaGallery_singleStageItemTemplate');
                    mediaGallery.slice(0, 4).forEach((mediaItem, index) => {
                        const $item = $template.clone();
                        
                        if(index === 3) {
                            $item.addClass("d-none d-md-block");
                        }

                        $item.find("[data-label='media-item-link']").attr("data-index", index);
                        $item.find("[data-label='media-item-thumbnail']").attr("src", mediaItem.media_url_thumbnail).attr('alt', mediaItem.caption || '');

                        if(mediaItem.media_type === "video") {
                            $item.find("[data-label='media-item-link']").addClass("video-thumbnail-corner-badge");
                        }

                        let first_three_classes = 'd-none';
                        if(index < 3) {
                            first_three_classes = 'd-flex';
                        }
                        if(index === 2) {
                            first_three_classes = 'd-none d-md-flex';
                        }
                        // Remove artifact from index=0 and then add the new classes.
                        $item.find("[data-label='media-item-zoom-icon']").removeClass("d-flex").addClass(first_three_classes);
                        
                        let after_first_two_classes = 'd-none';
                        if(index >= 2) {
                            after_first_two_classes = 'd-flex';
                        }
                        if(index === 2) {
                            after_first_two_classes = 'd-md-none d-flex';
                        }

                        // Remove artifact from index=0 and then add the new classes.
                        $item.find("[data-label='media-item-count-container']").removeClass("d-none").addClass(after_first_two_classes);
                        
                        $item.find("[data-label='media-item-count']").text('+ ' + (parseInt(window.ironman.postMediaGalleryCounts[galleryKey]) - index).toString());

                        new_media.push($item);
                    });
                }

                // Clear existing media items.
                $container.find("[data-label='uploaded-media-item']").remove();

                // Add the new media items to the container.
                $container.append(new_media);
            }
        });
    }
}

async function showEditCaptionPopup(galleryItemId, galleryKey) {
    log('showEditCaptionPopup', galleryItemId);
    
    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    // Update the Fenix modal with the Route Select modal content.
    const modalTitle = 'Add Comment';
    const modalFooter = getTemplate('fenixDefaultFooterTemplate'); // Use the default footer template and customize it.
    const modalDesktopSize = 'sm';
    const modalMobileHeight = 'short';
    const modalDataPurpose = 'editMediaCaption';
    
    // Customize the default modal footer...
    modalFooter.find('[data-label="fenixDefaultFooterSaveButton"]').text('Save Comment').attr('data-action', 'editMediaCaptionModal_submit').attr('data-media-id', galleryItemId);

    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    await openFenixModal();
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.
    
    let modalBody = getTemplate('editMediaCaptionModalTemplate');
    let newModalTitle = true;
    
    // Start customizing the modal body...
    if(galleryKey && galleryItemId) {
        // Get the gallery item by id and key.
        let galleryItem = null;
        $.each(window.ironman.postMediaGallery[galleryKey], function(index, item) {
            if(parseInt(item.id) === parseInt(galleryItemId)) {
                galleryItem = item;
                return false;
            }
        });
        if(galleryItem) {
            modalBody.find('[data-label="editMediaCaptionTextarea"]').val(galleryItem.caption || '');
            if(galleryItem.caption && galleryItem.caption.length > 0) {
                newModalTitle = 'Edit Comment';
            }
        }

        // Set the user avatar.
        // modalBody.find('[data-label="editMediaCaptionModal_userAvatar"]').attr('src', window.ironman.userData.userAvatarThumbnail || '');
    }
    // End customizing the modal body...

    // Set the modal content. True means don't change it - for use when the modal is already open.
    setFenixModalContent(newModalTitle, modalBody, true, true, true, true);

}

async function showReportMediaItemPopup(galleryItemId) {
    log('showReportMediaItemPopup', galleryItemId);
    
    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    // Update the Fenix modal with the Route Select modal content.
    const modalTitle = 'Report Media Item';
    const modalFooter = getTemplate('fenixDefaultFooterTemplate'); // Use the default footer template and customize it.
    const modalDesktopSize = 'sm';
    const modalMobileHeight = 'short';
    const modalDataPurpose = 'reportMediaItem';
    
    // Customize the default modal footer...
    modalFooter.find('[data-label="fenixDefaultFooterSaveButton"]').text('Submit Report').attr('data-action', 'reportMediaItemModal_submit').attr('data-media-id', galleryItemId);

    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    await openFenixModal();
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.
    
    let modalBody = getTemplate('reportMediaItemModalTemplate');
    let newModalTitle = true;
    
    // Start customizing the modal body...
    // if(galleryKey && galleryItemId) {
    //     // Get the gallery item by id and key.
    //     let galleryItem = null;
    //     $.each(window.ironman.postMediaGallery[galleryKey], function(index, item) {
    //         if(parseInt(item.id) === parseInt(galleryItemId)) {
    //             galleryItem = item;
    //             return false;
    //         }
    //     });
    // }

    
    // End customizing the modal body...

    // Set the modal content. True means don't change it - for use when the modal is already open.
    setFenixModalContent(newModalTitle, modalBody, true, true, true, true);

}